import React, { useState, useEffect, useCallback } from 'react';
import {
  User, Mail, Building2, Phone, MapPin, X, AlertCircle,
  Loader2, CheckCircle, Users, Calendar, FileText, Globe,
  CreditCard, Hash, Briefcase
} from 'lucide-react';
import { 
  createMember, listMembershipPlans, calculateMembershipFee, 
  getUnifiedPlanDetails 
} from '../../services/api';
import toast from 'react-hot-toast';
import { withLocalizedPlan } from '../../utils/membershipPlanLocalization';
import { getCurrentLanguage } from '../../services/dataSync';

const InputField = ({
  label,
  name,
  type = 'text',
  required = false,
  icon: Icon,
  placeholder,
  maxLength,
  value,
  error,
  onChange,
  ...props
}) => {
  const fieldValue = String(value || '');
  const hasError = !!error;

  const baseClass =
    'block w-full pr-3 py-2.5 border-2 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-primary-500 text-sm transition-all';
  const paddingLeft = Icon ? 'pl-10' : 'pl-4';
  const stateClass = hasError
    ? 'border-red-500 bg-red-50 focus:ring-red-300'
    : 'border-gray-300 bg-white focus:ring-primary-300';

  return (
    <div>
      <label
        htmlFor={name}
        className="block text-sm font-semibold text-gray-700 mb-1.5"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon
              className={`h-4 w-4 ${
                hasError ? 'text-red-500' : 'text-gray-400'
              }`}
            />
          </div>
        )}
        {type === 'textarea' ? (
          <textarea
            id={name}
            name={name}
            rows={props.rows || 3}
            maxLength={maxLength}
            className={`${baseClass} ${paddingLeft} ${stateClass}`}
            placeholder={placeholder}
            value={fieldValue}
            onChange={onChange}
            {...props}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={type}
            maxLength={maxLength}
            className={`${baseClass} ${paddingLeft} ${stateClass}`}
            placeholder={placeholder}
            value={fieldValue}
            onChange={onChange}
            {...props}
          />
        )}
      </div>
      {hasError && (
        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
};

const AdminCreateMemberForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    mobile: '',
    email: '',
    applicantName: '',
    representativeName: '',
    gender: '',
    telephone: '',
    aadhar: '',
    pancard: '',
    address: '',
    state: '',
    district: '',
    city: '',
    pincode: '',
    website: '',
    gst: '',
    natureOfBusiness: '',
    industry: '',
    workerCount: '',
    membershipPlanId: '',
    personName: '',
    organizationName: '',
    location: '',
    membershipType: '',
    startDate: '',
    endDate: '',
  });

  const [plans, setPlans] = useState([]);
  const [unifiedPlan, setUnifiedPlan] = useState(null);
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [calculatingFee, setCalculatingFee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [createPayment, setCreatePayment] = useState(false);

  const calculateEndDate = useCallback((duration, unit) => {
    if (!duration) return '';
    const date = new Date();
    const d = parseInt(duration, 10);
    if (unit === 'Months' || unit === 'months') {
      date.setMonth(date.getMonth() + d);
    } else if (unit === 'Years' || unit === 'years') {
      date.setFullYear(date.getFullYear() + d);
    }
    return date.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const data = await listMembershipPlans();
        const activePlans = data.filter(p => p.isActive).map(p => withLocalizedPlan(p, getCurrentLanguage()));
        setPlans(activePlans);

        const unified = activePlans.find(p => p.type === 'unified');
        if (unified) {
          setUnifiedPlan(unified);
          setFormData(prev => ({
            ...prev,
            membershipPlanId: unified._id || unified.id,
            membershipType: unified.name || '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: calculateEndDate(unified.duration, unified.durationUnit),
          }));
        } else {
          try {
            const unifiedDetails = withLocalizedPlan(await getUnifiedPlanDetails(), getCurrentLanguage());
            setUnifiedPlan(unifiedDetails);
            setFormData(prev => ({
              ...prev,
              membershipPlanId: unifiedDetails._id || unifiedDetails.id,
              membershipType: unifiedDetails.name || '',
              startDate: new Date().toISOString().split('T')[0],
              endDate: calculateEndDate(unifiedDetails.duration, unifiedDetails.durationUnit),
            }));
          } catch (err) {
            console.error('Failed to load unified plan:', err);
          }
        }
      } catch (error) {
        console.error('Failed to load plans:', error);
        toast.error('Failed to load membership plans');
      } finally {
        setIsLoadingPlans(false);
      }
    };

    loadPlans();
  }, [calculateEndDate]);

  const validateField = useCallback((name, rawValue) => {
    const value = (rawValue || '').trim();
    
    const requiredFields = [
      'mobile', 'email', 'applicantName', 'organizationName',
      'representativeName', 'gender', 'aadhar', 'pancard',
      'address', 'state', 'district', 'city', 'pincode',
      'natureOfBusiness', 'industry', 'membershipPlanId',
    ];
    
    if (!value && !requiredFields.includes(name)) {
      return '';
    }

    if (value) {
      switch (name) {
        case 'mobile':
          if (!/^[6-9]\d{9}$/.test(value) || value.length !== 10) {
            return 'Please enter a valid 10-digit mobile number';
          }
          break;
        case 'email':
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return 'Please enter a valid email address';
          }
          break;
        case 'applicantName':
        case 'organizationName':
        case 'representativeName':
          if (value.length < 2) {
            return 'This field is required';
          }
          break;
        case 'aadhar':
          if (value.length !== 12 || !/^\d{12}$/.test(value)) {
            return 'Please enter a valid 12-digit Aadhar number';
          }
          break;
        case 'pancard':
          if (value.length !== 10 || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase())) {
            return 'Please enter a valid PAN card number';
          }
          break;
        case 'pincode':
          if (value.length !== 6 || !/^\d{6}$/.test(value)) {
            return 'Please enter a valid 6-digit pincode';
          }
          break;
        case 'gst':
          if (value && value.length === 15 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase())) {
            return 'Please enter a valid GST number';
          }
          break;
        case 'website':
          if (value) {
            try {
              const url = value.startsWith('http') ? value : `https://${value}`;
              new URL(url);
            } catch {
              return 'Please enter a valid URL';
            }
          }
          break;
        case 'workerCount':
          const workerCountNum = parseInt(value, 10);
          if (isNaN(workerCountNum) || workerCountNum < 1) {
            return 'Please enter a valid worker count (at least 1)';
          }
          break;
        default:
          break;
      }
    }

    return '';
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'mobile', 'email', 'applicantName', 'organizationName',
      'representativeName', 'gender', 'aadhar', 'pancard',
      'address', 'state', 'district', 'city', 'pincode',
      'natureOfBusiness', 'industry', 'membershipPlanId',
    ];

    requiredFields.forEach(field => {
      if (field === 'gender') {
        if (!formData[field]) {
          newErrors[field] = 'This field is required';
        }
      } else if (!formData[field] || formData[field].trim().length === 0) {
        newErrors[field] = 'This field is required';
      } else {
        const err = validateField(field, formData[field]);
        if (err) newErrors[field] = err;
      }
    });

    // Validate worker count if provided
    if (formData.workerCount) {
      const err = validateField('workerCount', formData.workerCount);
      if (err) newErrors.workerCount = err;
    }

    // Validate optional fields only if they have values
    ['telephone', 'gst', 'website'].forEach(field => {
      const v = formData[field];
      if (v && v.trim()) {
        const err = validateField(field, v);
        if (err) newErrors[field] = err;
      }
    });

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (['mobile', 'aadhar', 'pincode', 'telephone'].includes(name)) {
      processedValue = value.replace(/\D/g, '');
    } else if (name === 'pancard' || name === 'gst') {
      processedValue = value.toUpperCase();
    }

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: processedValue,
      };

      if (name === 'applicantName') {
        updated.personName = processedValue;
      }
      if (name === 'address') {
        updated.location = processedValue;
      }

      // Handle worker count change and fee calculation
      if (name === 'workerCount' && processedValue) {
        const workerCountNum = parseInt(processedValue, 10);
        if (!isNaN(workerCountNum) && workerCountNum > 0) {
          setCalculatingFee(true);
          calculateMembershipFee(workerCountNum)
            .then(feeData => {
              setFeeCalculation(feeData);
              setCalculatingFee(false);
            })
            .catch(err => {
              console.error('Failed to calculate fee:', err);
              setFeeCalculation(null);
              setCalculatingFee(false);
            });
        } else {
          setFeeCalculation(null);
        }
      }

      return updated;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const validation = validateForm();
    if (!validation.isValid) {
      setErrorMessage('Please fix the errors below');
      setTimeout(() => {
        const firstErrorField = Object.keys(validation.errors)[0];
        if (firstErrorField) {
          const element = document.getElementById(firstErrorField);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return;
    }

    setSubmitting(true);

    try {
      const selectedPlanData = unifiedPlan || plans.find(
        p => (p._id || p.id) === formData.membershipPlanId,
      );
      if (!selectedPlanData) {
        throw new Error('Membership plan not found. Please refresh and try again.');
      }

      const workerCountNum = parseInt(formData.workerCount, 10);
      if (!workerCountNum || workerCountNum < 1) {
        throw new Error('Please enter a valid worker count (at least 1)');
      }

      const membershipFee = feeCalculation?.fee || selectedPlanData.price || 0;

      const payload = {
        personName: (formData.applicantName || formData.personName || '').trim(),
        organizationName: (formData.organizationName || '').trim(),
        email: (formData.email || '').trim(),
        mobile: (formData.mobile || '').trim(),
        location: (formData.address || formData.location || '').trim(),
        industry: (formData.industry || '').trim(),
        natureOfBusiness: (formData.natureOfBusiness || '').trim(),
        workerCount: workerCountNum,
        membershipPlanId: formData.membershipPlanId || (selectedPlanData._id || selectedPlanData.id),
        membershipPlanName: selectedPlanData.name,
        representativeName: (formData.representativeName || '').trim(),
        gender: formData.gender || '',
        telephone: (formData.telephone || '').trim() || undefined,
        aadhar: (formData.aadhar || '').trim(),
        pancard: (formData.pancard || '').trim(),
        state: (formData.state || '').trim(),
        district: (formData.district || '').trim(),
        city: (formData.city || '').trim(),
        pincode: (formData.pincode || '').trim(),
        website: formData.website && formData.website.trim() ? formData.website.trim() : undefined,
        gst: (formData.gst || '').trim() || undefined,
        membershipType: (formData.membershipType || '').trim(),
        startDate: formData.startDate || '',
        endDate: formData.endDate || '',
      };

      const created = await createMember(payload);
      
      toast.success('Member created successfully!');
      
      if (onSuccess) {
        onSuccess(created);
      }
    } catch (err) {
      console.error('Member creation error:', err);
      let errorMsg = 'Failed to create member. Please try again.';
      if (err.message) {
        errorMsg = err.message;
      } else if (err.data) {
        errorMsg = err.data.message || err.data.error || err.message || errorMsg;
      }

      if (errorMsg.toLowerCase().includes('email') && 
          (errorMsg.toLowerCase().includes('already') || 
           errorMsg.toLowerCase().includes('exists') || 
           errorMsg.toLowerCase().includes('duplicate'))) {
        setErrors(prev => ({
          ...prev,
          email: 'This email address is already registered.'
        }));
        setTimeout(() => {
          const emailField = document.getElementById('email');
          if (emailField) {
            emailField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            emailField.focus();
          }
        }, 100);
      }

      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoadingPlans) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Loading membership plans...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden min-w-0">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Create New Member</h2>
          <p className="text-sm text-gray-600 mt-1">Add a new member to the system</p>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        )}
      </div>

      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-gray-50 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-primary-600" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <InputField
              label="Applicant Name"
              name="applicantName"
              icon={User}
              required
              value={formData.applicantName}
              error={errors.applicantName}
              onChange={handleChange}
              placeholder="Enter applicant name"
            />
            <InputField
              label="Representative Name"
              name="representativeName"
              icon={User}
              required
              value={formData.representativeName}
              error={errors.representativeName}
              onChange={handleChange}
              placeholder="Enter representative name"
            />
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Gender <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={formData.gender === 'Male'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Male</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={formData.gender === 'Female'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Female</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="Other"
                    checked={formData.gender === 'Other'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Other</span>
                </label>
              </div>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.gender}
                </p>
              )}
            </div>
            <InputField
              label="Aadhar Number"
              name="aadhar"
              icon={Hash}
              required
              maxLength={12}
              value={formData.aadhar}
              error={errors.aadhar}
              onChange={handleChange}
              placeholder="Enter 12-digit Aadhar number"
            />
            <InputField
              label="PAN Card"
              name="pancard"
              icon={FileText}
              required
              maxLength={10}
              value={formData.pancard}
              error={errors.pancard}
              onChange={handleChange}
              placeholder="Enter PAN card number"
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary-600" />
            Contact Information
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <InputField
              label="Email"
              name="email"
              type="email"
              icon={Mail}
              required
              value={formData.email}
              error={errors.email}
              onChange={handleChange}
              placeholder="Enter email address"
            />
            <InputField
              label="Mobile"
              name="mobile"
              icon={Phone}
              required
              maxLength={10}
              value={formData.mobile}
              error={errors.mobile}
              onChange={handleChange}
              placeholder="Enter 10-digit mobile number"
            />
            <InputField
              label="Telephone (Optional)"
              name="telephone"
              icon={Phone}
              maxLength={15}
              value={formData.telephone}
              error={errors.telephone}
              onChange={handleChange}
              placeholder="Enter telephone number"
            />
          </div>
        </div>

        {/* Organization Information */}
        <div className="bg-gray-50 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary-600" />
            Organization Information
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <InputField
              label="Organization Name"
              name="organizationName"
              icon={Building2}
              required
              value={formData.organizationName}
              error={errors.organizationName}
              onChange={handleChange}
              placeholder="Enter organization name"
            />
            <InputField
              label="Nature of Business"
              name="natureOfBusiness"
              icon={Briefcase}
              required
              value={formData.natureOfBusiness}
              error={errors.natureOfBusiness}
              onChange={handleChange}
              placeholder="Enter nature of business"
            />
            <InputField
              label="Industry"
              name="industry"
              icon={Briefcase}
              required
              value={formData.industry}
              error={errors.industry}
              onChange={handleChange}
              placeholder="Enter industry"
            />
            <InputField
              label="GST Number (Optional)"
              name="gst"
              icon={Hash}
              maxLength={15}
              value={formData.gst}
              error={errors.gst}
              onChange={handleChange}
              placeholder="Enter GST number"
            />
            <InputField
              label="Website (Optional)"
              name="website"
              icon={Globe}
              value={formData.website}
              error={errors.website}
              onChange={handleChange}
              placeholder="Enter website URL"
            />
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-gray-50 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary-600" />
            Address Information
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <InputField
              label="Address"
              name="address"
              type="textarea"
              icon={MapPin}
              required
              rows={3}
              value={formData.address}
              error={errors.address}
              onChange={handleChange}
              placeholder="Enter complete address"
            />
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <InputField
                label="State"
                name="state"
                required
                value={formData.state}
                error={errors.state}
                onChange={handleChange}
                placeholder="Enter state"
              />
              <InputField
                label="District"
                name="district"
                required
                value={formData.district}
                error={errors.district}
                onChange={handleChange}
                placeholder="Enter district"
              />
              <InputField
                label="City"
                name="city"
                required
                value={formData.city}
                error={errors.city}
                onChange={handleChange}
                placeholder="Enter city"
              />
            </div>
            <InputField
              label="Pincode"
              name="pincode"
              icon={Hash}
              required
              maxLength={6}
              value={formData.pincode}
              error={errors.pincode}
              onChange={handleChange}
              placeholder="Enter 6-digit pincode"
            />
          </div>
        </div>

        {/* Membership Information */}
        <div className="bg-gray-50 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary-600" />
            Membership Information
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Membership Plan <span className="text-red-500">*</span>
              </label>
              <select
                name="membershipPlanId"
                value={formData.membershipPlanId}
                onChange={handleChange}
                className={`block w-full py-2.5 px-4 border-2 rounded-lg focus:outline-none focus:ring-2 focus:border-primary-500 text-sm transition-all ${
                  errors.membershipPlanId
                    ? 'border-red-500 bg-red-50 focus:ring-red-300'
                    : 'border-gray-300 bg-white focus:ring-primary-300'
                }`}
              >
                <option value="">Select a plan</option>
                {plans.map(plan => (
                  <option key={plan._id || plan.id} value={plan._id || plan.id}>
                    {plan.name} - {plan.duration} {plan.durationUnit}
                  </option>
                ))}
              </select>
              {errors.membershipPlanId && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.membershipPlanId}
                </p>
              )}
            </div>
            <InputField
              label="Number of Workers"
              name="workerCount"
              type="number"
              icon={Users}
              required
              min="1"
              value={formData.workerCount}
              error={errors.workerCount}
              onChange={handleChange}
              placeholder="Enter number of workers"
            />
            {calculatingFee && (
              <div className="col-span-2 flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Calculating membership fee...</span>
              </div>
            )}
            {feeCalculation && !calculatingFee && (
              <div className="col-span-2 bg-primary-50 border border-primary-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Membership Fee</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {feeCalculation.breakdown?.calculation || `₹${feeCalculation.fee.toLocaleString('en-IN')}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary-600">
                      ₹{feeCalculation.fee.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {feeCalculation.pricingCategory}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <InputField
              label="Start Date"
              name="startDate"
              type="date"
              icon={Calendar}
              value={formData.startDate}
              onChange={handleChange}
            />
            <InputField
              label="End Date"
              name="endDate"
              type="date"
              icon={Calendar}
              value={formData.endDate}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-4 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating Member...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Create Member
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateMemberForm;

